import moment from "moment";
import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";

import { formatName } from "../../../../common/format-name";
import { PatientService } from "../../../../services/patient.service";
import { enumToMap } from "../../../../common/vue-helpers/modifiers/enum-modifiers";
import { TaskService } from "../../../../services/task.service";
import { HHDiscipline } from "../../../../enums/HHDisciplineEnum";
import config from "../../../../common/config";
import { formatDate } from "../../../../common/vue-helpers/modifiers/value-modifier";

import EmptyTextHandler from "../../../../resources-vue/vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";
import ErrorMessageHandler from "../../../../resources-vue/vue-custom-elements/ErrorMessageHandler/ErrorMessageHandler.vue";
import HHProfileDetails from "./HHProfileDetails/HHProfileDetails.vue";
import DateRangePicker from "../../../../resources-vue/vue-custom-elements/DateRangePicker/DateRangePicker.vue";
import HHTaskRow from "./HHTaskRow/HHTaskRow.vue";
import SortableTh from "../../../../resources-vue/vue-custom-elements/SortableTh/SortableTh.vue";

export default defineComponent({
    components: {
        EmptyTextHandler,
        ErrorMessageHandler,
        HHProfileDetails,
        DateRangePicker,
        HHTaskRow,
        SortableTh
    },
    props: {
        selectedPatientDetails: {
            default: null
        },
        agencyId: {
            type: String
        },
        application: {
            type: Number
        },
        patientId: {
            type: String
        },
        patientRiskDetails: {
            default: null
        },
        patientTags: {
            type: Array,
            default: []
        },
        patientDetails: {
            default: null
        }
    },
    data(vm) {
        return {
            _taskService: null as TaskService,
            _patientService: null as PatientService,
            disciplines: enumToMap(HHDiscipline),
            selectedDiscipline: null as number,
            isLoading: false,
            allTasks: [],
            isError: false,
            episodeList: [],
            selectedEpisode: "All",
            columns: 7,
            showDateRange: false,
            range: "",
            selectedEpisodeDetails: null,
            dateFrom: moment().subtract(60, "days").format("MM/DD/YYYY") as string,
            dateTo: moment().format("MM/DD/YYYY") as string,
            newDateRange: (moment().subtract(60, "days").format("MM/DD/YYYY") as string)
                .concat("-")
                .concat(moment().format("MM/DD/YYYY") as string),
            sortBy: "TaskName",
            sortOrder: "asc",
            moment,
            formatName,
            formatDate
        };
    },

    async created() {
        this._taskService = Container.instance.get(TaskService);
        this._patientService = Container.instance.get(PatientService);
    },

    mounted() {
        this.getBenefitPeriods();
        this.range = "";
        this.showDateRange = false;
        this.selectedEpisodeDetails = {
            startDate: this.selectedPatientDetails.StartofCareDate,
            endDate: ""
        };
        this.getPatientTasks();
    },

    computed: {
        hasData() {
            return this.allTasks.length > 0;
        },
        loadingCount() {
            return this.allTasks.length > 0 ? this.allTasks.length : config.pageSize;
        },
        showRangeText(): boolean {
            let dateRange = ["All", "DateRange"];
            return !dateRange.includes(this.selectedEpisode);
        }
    },

    watch: {
        selectedDiscipline: {
            handler() {
                this.getPatientTasks();
            },
            deep: true
        }
    },

    methods: {
        async getPatientTasks() {
            try {
                this.isLoading = true;
                const payload = {
                    AgencyId: this.agencyId,
                    PatientId: this.patientId,
                    StartDate: this.showDateRange ? this.dateFrom : this.selectedEpisodeDetails.startDate,
                    EndDate: this.showDateRange ? this.dateTo : this.selectedEpisodeDetails.endDate,
                    Order: this.sortOrder,
                    SortBy: this.sortBy,
                    Discipline: this.selectedDiscipline ? this.selectedDiscipline : null,
                    Status: this.selectedPatientDetails.Status,
                    DateRangeId: this.selectedEpisode
                };
                this.allTasks = (await this._taskService.getTasksByPatient(payload)) as any;
            } catch (err) {
                this.isError = true;
                console.error(err);
            } finally {
                this.isLoading = false;
            }
        },
        async getBenefitPeriods() {
            const payload = {
                agencyId: this.agencyId,
                application: this.application,
                patientId: this.patientId
            };
            const response = (await this._patientService.getBenefitPeriods(payload)) as any;
            this.episodeList = [...this.episodeList, ...response];
        },

        handleSelectDateRange() {
            if (this.selectedEpisode == "DateRange") {
                this.showDateRange = true;
                this.range = "";
                this.getPatientTasks();
            } else {
                if (this.selectedEpisode == "All") {
                    this.range = "";
                    this.showDateRange = false;
                    this.selectedEpisodeDetails = {
                        startDate: this.selectedPatientDetails.StartofCareDate,
                        endDate: ""
                    };
                } else {
                    this.showDateRange = false;
                    const data = this.episodeList.find((episode) => episode.title === this.selectedEpisode);
                    this.selectedEpisodeDetails = data;
                    this.range = `${moment(data.startDate).format("MM/DD/YYYY")} - ${moment(data.endDate).format("MM/DD/YYYY")}`;
                }
                this.getPatientTasks();
            }
        },
        setnewTaskDateRange(value: any) {
            this.dateFrom = String(value.split("-")[0]).trim();
            this.dateTo = String(value.split("-")[1]).trim();
            this.newDateRange = value;
            this.getPatientTasks();
        },
        sortByUpdated(value: string) {
            if (value != this.sortBy) {
                this.sortBy = value;
                this.getPatientTasks();
            }
        },

        sortOrderUpdated(value: string) {
            if (value != this.sortOrder) {
                this.sortOrder = value;
                this.getPatientTasks();
            }
        }
    }
});
